/* eslint-disable max-statements */
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Subscription } from '@adornis/baseql/decorators.js';
import { registerQuery } from '@adornis/baseql/metadata/register';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Permissions } from '@adornis/digitale-helden-shared/db/permissions';
import { ActionTokenData } from '@adornis/digitale-helden-shared/features/action-tokens/db/ActionTokenData.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter';
import { _subscribeCurrentUser, updateCurrentUser } from '@adornis/users/client/currentUser';
import { AdornisUser } from '@adornis/users/db/a-user';
import { emailRegexCheck, nameRegexCheck } from '@adornis/users/db/validators';
import { setUserOnContext } from '@adornis/users/server/baseqlContextHelper';
import { hashPassword } from '@adornis/users/server/password';
import { BaseError } from '@adornis/validation/errors/BaseError';
import { switchMap } from 'rxjs';
import { getContactByIDCOQL } from '../api/Contact/getContactByIDCOQL';
import { getLASUserByEmail } from '../features/users/api/getUserByEmail';

export const CONFIRM_EMAIL_ERROR_MESSAGE = 'Du musst deine E-Mail-Adresse zuvor bestätigen.';

export async function customLogin(usernameOrMail: string, password: string, loginTTL?: number) {
  if (!nameRegexCheck(usernameOrMail) && !emailRegexCheck(usernameOrMail))
    throw new BaseError('Error in login(): Provided usernameOrMail is no valid name nor email', {
      translationKey: 'users.invalid_username',
    });
  if (!password) throw new BaseError('No password given', { translationKey: 'users.no_password' });
  const [userID, userClass] = await customLoginWithPassword(usernameOrMail, password)();
  updateCurrentUser(void 0, false);
  await _subscribeCurrentUser({ userID, userClass });
}

const customLoginWithPasswordResolver = (email: string, password: string) => async () => {
  const collection = await getRawCollection<LASUser>(LASUser._collectionName);
  const passHash = hashPassword(password);
  if (!nameRegexCheck(email) && !emailRegexCheck(email))
    throw new BaseError('loginWithPassword(): Provided usernameOrMail is neither an username nor email', {
      translationKey: 'users.invalid_username_and_email',
    });
  const user = await collection.findOne<LASUser>(
    {
      $or: [
        { username: { $regex: `^${email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' } },
        { email: { $regex: `^${email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' } },
      ],
      password: passHash,
    },
    { projection: { _id: 1, _class: 1, zohoID: 1 } },
  );
  if (!user)
    throw new BaseError('Username/Email or password is wrong', {
      translationKey: 'users.invalid_credentials',
    });

  const contact = await getContactByIDCOQL(user.zohoID)({ id: 1, emailStatus: 1 });
  if (!contact || !contact.hasEmailConfirmed()) throw new Error(CONFIRM_EMAIL_ERROR_MESSAGE);
  await setUserOnContext(user);
  return [user._id, user._class];
};

export const customLoginWithPassword = registerQuery({
  type: () => [String],
  operationName: 'customLoginWithPassword',
  resolve: customLoginWithPasswordResolver,
  options: {},
  params: [
    { type: () => String, name: 'email' },
    { type: () => String, name: 'password' },
  ],
});

const customLoginWithTokenResolver = (tokenID: string) => async () => {
  const rawTokenCollection = await getRawCollection<ActionTokenData>(ActionTokenData._collectionName);
  const token = await rawTokenCollection.findOne<ActionTokenData>({ _id: tokenID });
  if (!token) return [];
  const email = token.contact?.email;
  const user = await getLASUserByEmail(email)(LASUser.allFields);
  console.log('user found: ', user);
  if (!user) return [];
  await setUserOnContext(user);
  return [user._id, user._class];
};

export const customLoginWithToken = registerQuery({
  type: () => [String],
  operationName: 'customLoginWithToken',
  resolve: customLoginWithTokenResolver,
  options: {},
  params: [{ type: () => String, name: 'tokenID' }],
});

/**
 * @entity LASUser
 */
@Entity()
export class LASUser extends AdornisUser<any> {
  static override _class = 'LASUser';

  // de facto the zoho contact ID
  @Field(type => ID)
  zohoID!: string;

  hasRole(role: Permissions.UserRoles) {
    return !!this.roles.find(r => r.name === role);
  }

  serializeZoho = () => {
    const info = JSON.stringify({
      data: [
        {
          Email: this.email,
          Last_Name: this.username,
          Double_Opt_In: this.emailConfirmed,
        },
      ],
    });
    return info;
  };

  @Subscription(type => [LASUser])
  static subscribeLASUsers(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => AdornisFilter) filter: AdornisFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<LASUser>) => {
      const filterObject = filter.toObject();
      if (filterObject.search) filterObject.search = filterObject.search.replace(/([()[{*+.$^\\|?])/g, '\\$1');

      const query = filterObject.search
        ? {
            $or: [
              { username: { $regex: filterObject.search, $options: 'i' } },
              { email: { $regex: filterObject.search, $options: 'i' } },
            ],
          }
        : {};

      const sorting = {};

      return getCollectionHandle<LASUser>(LASUser._collectionName)
        .watchQuery({})
        .pipe(
          switchMap(async e => {
            const pubFilter = await publishFilter(this._class);
            const filtering = pubFilter ? { $and: [pubFilter, query] } : query;

            const collection = await getCollection<LASUser>(LASUser._class);
            const result = await collection
              .find(filtering, {
                sort: sorting,
                skip,
                limit,
              })
              .toArray();
            return result;
          }),
        );
    };
  }
}
